import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 ────────────────────────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

const Ico = {
  Mic:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
  Book: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  Tag:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  Quiz: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

// ── 요약 텍스트를 섹션/문제 단위로 예쁘게 렌더링 ──────────────
function renderSummaryBlocks(fullSummary, isMobile) {
  if (!fullSummary) return null;

  const lines = fullSummary.split('\n').map(l => l.trim());

  const isSectionHeader = (line) =>
    /^\[.+\]$/.test(line) ||
    /^#{1,3}\s/.test(line) ||
    line.includes('강의 요약') ||
    line.includes('예상 시험 문제') ||
    line.includes('예상문제') ||
    line.includes('시험 문제');

  const isQuestionStart = (line) =>
    /^(?:문제\s*|Q\s*)?(\d+)[.)]\s*.+/i.test(line);

  const fontSize = isMobile ? '12px' : '13px';
  const elements = [];
  let i = 0;
  let inExamSection = false;
  let questionBuffer = [];
  let qIndex = 0;

  // ✅ 정답 토글 상태를 각 문제별로 관리하는 컴포넌트
  function QuestionBlock({ lines, qIdx, isMobile }) {
    const [open, setOpen] = React.useState(false);
    const fontSize = isMobile ? '12px' : '13px';

    // 정답 줄 분리
    const answerLineRe = /^정답\s*[:：]/;
    const answerLines = lines.filter(l => answerLineRe.test(l));
    const questionLines = lines.filter(l => !answerLineRe.test(l));

    return (
      <div style={{
        backgroundColor: '#f0f7ff',
        border: '1px solid #bfdbfe',
        borderRadius: '10px',
        padding: isMobile ? '10px 12px' : '12px 16px',
        marginBottom: isMobile ? '8px' : '10px',
      }}>
        {/* 문제 내용 */}
        {questionLines.map((qLine, qi) => (
          <p key={qi} style={{
            margin: qi === 0 ? '0 0 4px' : '2px 0 0',
            fontSize,
            color: qi === 0 ? '#1e293b' : '#374151',
            fontWeight: qi === 0 ? '600' : '400',
            lineHeight: '1.7',
          }}>{qLine}</p>
        ))}

        {/* ✅ 정답 토글 버튼 */}
        <button
          onClick={() => setOpen(v => !v)}
          style={{
            marginTop: '8px',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: open ? '#1e40af' : BLUE,
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          {open ? '정답 숨기기 ▲' : '정답 보기 ▼'}
        </button>

        {/* ✅ 정답 내용 */}
        {open && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#dbeafe',
            borderRadius: '6px',
            fontSize,
            color: '#1e40af',
            lineHeight: '1.7',
          }}>
            {answerLines.length > 0
              ? answerLines.map((l, idx) => <p key={idx} style={{ margin: 0 }}>{l}</p>)
              : <p style={{ margin: 0 }}>강의 요약의 핵심 개념을 중심으로 서술해보세요!</p>
            }
          </div>
        )}
      </div>
    );
  }

  const flushQuestion = () => {
    if (questionBuffer.length === 0) return;
    qIndex++;
    const capturedLines = [...questionBuffer];
    const capturedIdx = qIndex;
    const capturedMobile = isMobile;
    elements.push(
      <QuestionBlock key={`q-${capturedIdx}`} lines={capturedLines} qIdx={capturedIdx} isMobile={capturedMobile} />
    );
    questionBuffer = [];
  };

  while (i < lines.length) {
    const line = lines[i];
    if (!line) { i++; continue; }

    if (isSectionHeader(line)) {
      flushQuestion();
      const isExam = line.includes('예상') || line.includes('시험');
      inExamSection = isExam;
      const label = line.replace(/^#+\s*/, '').replace(/^\[|\]$/g, '');
      elements.push(
        <div key={`section-${i}`} style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          margin: elements.length === 0 ? '0 0 12px' : `${isMobile ? '18px' : '24px'} 0 12px`,
          paddingBottom: '8px',
          borderBottom: '2px solid #e2e8f0',
        }}>
          <span style={{ fontSize: isMobile ? '14px' : '16px' }}>{isExam ? '📝' : '📚'}</span>
          <span style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: '#1e293b' }}>
            {label || (isExam ? '예상 시험 문제' : '강의 요약')}
          </span>
        </div>
      );
      i++;
      continue;
    }

    if (inExamSection && isQuestionStart(line)) {
      flushQuestion();
      questionBuffer.push(line);
      i++;
      // ✅ 다음 문제 시작 or 섹션 헤더 전까지 모두 같은 박스에 (단답형/서술형 포함)
      while (
        i < lines.length &&
        lines[i] &&
        !isQuestionStart(lines[i]) &&
        !isSectionHeader(lines[i])
      ) {
        questionBuffer.push(lines[i]);
        i++;
      }
      continue;
    }

    flushQuestion();
    elements.push(
      <p key={`line-${i}`} style={{
        margin: '0 0 6px',
        fontSize,
        color: '#374151',
        lineHeight: '1.75',
      }}>{line}</p>
    );
    i++;
  }

  flushQuestion();
  return elements;
}

function SummaryPage() {
  const navigate  = useNavigate();
  const isMobile  = useIsMobile();

  const [summaryData,          setSummaryData]          = useState(null);
  const [transcriptExpanded,   setTranscriptExpanded]   = useState(false);
  const [showAlertModal,       setShowAlertModal]       = useState(false);
  const [alertMsg,             setAlertMsg]             = useState('');

  useEffect(() => {
    const summary    = localStorage.getItem('summary');
    const transcript = localStorage.getItem('transcript');

    if (!summary || !transcript) {
      setAlertMsg('분석 결과가 없습니다. 파일을 먼저 업로드해주세요.');
      setShowAlertModal(true);
      return;
    }

    const lines = summary.split('\n').filter(l => l.trim() !== '');

    const keywords = (() => {
      const words = transcript.split(/\s+/).filter(w => w.length > 2);
      const cnt   = {};
      words.forEach(w => { cnt[w] = (cnt[w] || 0) + 1; });
      return Object.entries(cnt)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([w]) => w.replace(/[.,!?]/g, ''));
    })();

    setSummaryData({ fullSummary: summary, lines, keywords, transcript });
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('spin-style-summary')) {
      const style       = document.createElement('style');
      style.id          = 'spin-style-summary';
      style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }
  }, []);

  if (!summaryData && !showAlertModal) {
    return (
      <div style={S.root}>
        <div style={S.loadingWrap}>
          <div style={S.spinner} />
          <p style={S.loadingText}>분석 결과를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...S.root, overflowX: 'hidden' }}>

      {/* ── 알림 모달 ── */}
      {showAlertModal && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, padding: isMobile ? '24px 20px' : '32px 40px' }}>
            <p style={S.modalMsg}>{alertMsg}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button style={S.modalBtn} onClick={() => { setShowAlertModal(false); navigate('/upload'); }}>확인</button>
            </div>
          </div>
        </div>
      )}

      {summaryData && (
        <main style={{
          ...S.main,
          padding: isMobile ? '24px 14px 60px' : '60px 20px',
          gap: isMobile ? '14px' : '20px',
        }}>

          {/* 페이지 타이틀 */}
          <div style={S.pageTitle}>
            <h2 style={{ ...S.pageTitleText, fontSize: isMobile ? '18px' : '26px' }}>
              ✨ AI 강의 요약 결과
            </h2>
            <p style={{ ...S.pageTitleSub, fontSize: isMobile ? '12px' : '14px' }}>
              업로드하신 파일의 핵심 분석 내용입니다.
            </p>
          </div>

          {/* ── 카드 1: 음성 인식 원문 ── */}
          <div style={{
            ...S.card,
            maxWidth: isMobile ? '100%' : '800px',
            padding: isMobile ? '16px 14px' : '22px 24px',
          }}>
            <CardHeader icon={Ico.Mic} title="음성 인식 원문" />
            <div style={S.transcriptBox}>
              <div style={{
                ...S.transcriptHeader,
                // 모바일: 버튼 오른쪽 정렬 유지
                flexWrap: 'wrap', gap: '6px',
              }}>
                <p style={S.transcriptLabel}>원문 텍스트</p>
                <button style={S.toggleBtn} onClick={() => setTranscriptExpanded(v => !v)}>
                  {transcriptExpanded ? '접기 ▲' : '전체 보기 ▼'}
                </button>
              </div>
              {transcriptExpanded ? (
                <p style={S.transcriptFull}>{summaryData.transcript}</p>
              ) : (
                <p style={S.transcriptPreview}>
                  {summaryData.transcript.split(/\s+/).slice(0, 20).join(' ')}
                  {summaryData.transcript.split(/\s+/).length > 20 ? ' ...' : ''}
                </p>
              )}
            </div>
          </div>

          {/* ── 카드 2: AI 요약 + 키워드 ── */}
          <div style={{
            ...S.card,
            maxWidth: isMobile ? '100%' : '800px',
            padding: isMobile ? '16px 14px' : '22px 24px',
          }}>
            <CardHeader icon={Ico.Book} title="AI 강의 요약" />
            <div style={S.summaryBox}>
              {renderSummaryBlocks(summaryData.fullSummary, isMobile)}
            </div>

            {summaryData.keywords.length > 0 && (
              <>
                <div style={{ ...S.cardHeader, marginTop: '18px' }}>
                  <div style={S.cardIcon}>{Ico.Tag}</div>
                  <h2 style={S.cardTitle}>핵심 키워드</h2>
                </div>
                <div style={S.tagWrap}>
                  {summaryData.keywords.map((kw, i) => (
                    <span key={i} style={S.tag}>#{kw}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── 카드 3: 다음 단계 ── */}
          <div style={{
            ...S.card,
            maxWidth: isMobile ? '100%' : '800px',
            padding: isMobile ? '16px 14px' : '22px 24px',
          }}>
            <CardHeader icon={Ico.Quiz} title="다음 단계" />
            <p style={{ ...S.nextStepDesc, fontSize: isMobile ? '12px' : '13px' }}>
              요약 내용을 바탕으로 AI가 생성한 예상 문제를 풀어보세요.
            </p>
            <div style={{ ...S.btnGroup, flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                style={{ ...S.secondaryBtn, fontSize: isMobile ? '13px' : '15px', padding: isMobile ? '12px' : '14px' }}
                onClick={() => navigate('/main')}
              >
                홈으로 이동
              </button>
              <button
                style={{ ...S.primaryBtn, fontSize: isMobile ? '13px' : '15px', padding: isMobile ? '12px' : '14px' }}
                onClick={() => navigate('/quiz')}
              >
                예상 문제 풀기 📝
              </button>
            </div>
          </div>

        </main>
      )}
    </div>
  );
}

// ── 스타일 (원본 그대로) ─────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh',
    backgroundColor: '#f4f7f9',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
  },
  main: {
    padding: '60px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  pageTitle:     { textAlign: 'center', marginBottom: '4px' },
  pageTitleText: { fontSize: '26px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px' },
  pageTitleSub:  { fontSize: '14px', color: '#64748b', margin: 0 },
  card: {
    backgroundColor: '#fff',
    borderRadius: '14px',
    padding: '22px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9',
    width: '100%',
    maxWidth: '800px',
    boxSizing: 'border-box',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' },
  cardIcon: {
    width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: BLUE_BG, color: BLUE,
  },
  cardTitle:          { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  transcriptBox:      { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px' },
  transcriptHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  transcriptLabel:    { fontSize: '10px', fontWeight: '600', color: '#94a3b8', letterSpacing: '0.5px', textTransform: 'uppercase', margin: 0 },
  toggleBtn:          { padding: '4px 10px', fontSize: '11px', fontWeight: '600', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' },
  transcriptPreview:  { fontSize: '13px', color: '#64748b', lineHeight: '1.6', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  transcriptFull:     { fontSize: '13px', color: '#374151', lineHeight: '1.8', margin: 0 },
  summaryBox:         { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px' },
  summaryLine:        { fontSize: '13px', lineHeight: '1.8', color: '#374151', margin: '3px 0' },
  tagWrap:            { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  tag:                { padding: '4px 10px', backgroundColor: BLUE_BG, color: BLUE, borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  nextStepDesc:       { margin: '0 0 14px', fontSize: '13px', color: '#64748b', lineHeight: '1.6' },
  btnGroup:           { display: 'flex', gap: '12px' },
  primaryBtn:         { flex: 1, padding: '14px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn:       { flex: 1, padding: '14px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  loadingWrap:        { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 20px', gap: '14px' },
  spinner:            { width: '28px', height: '28px', border: '3px solid #bfdbfe', borderTop: `3px solid ${BLUE}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText:        { fontSize: '14px', color: '#64748b', margin: 0 },
  modalOverlay:       { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
  modal:              { backgroundColor: '#fff', padding: '32px 40px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', textAlign: 'center', minWidth: '280px', width: '90%', maxWidth: '360px', boxSizing: 'border-box' },
  modalMsg:           { fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: '0 0 24px', lineHeight: '1.5' },
  modalBtn:           { padding: '9px 24px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

if (typeof document !== 'undefined' && !document.getElementById('spin-style-summary')) {
  const style       = document.createElement('style');
  style.id          = 'spin-style-summary';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default SummaryPage;